package com.vizio.redwolf.dms.homesetup

import com.vizio.redwolf.auth.api.AccountApi
import com.vizio.redwolf.auth.model.AccountAuthCodeRequestInfo
import com.vizio.redwolf.auth.model.AccountResult
import com.vizio.redwolf.dms.LinkedDevicesFailureMessage
import com.vizio.redwolf.dms.LinkedDevicesResponse
import com.vizio.redwolf.dms.NonceJsonConfig
import com.vizio.redwolf.dms.model.FieldedStatusRequestInfo
import com.vizio.redwolf.dms.model.LinkNonceRequestInfo
import com.vizio.redwolf.dms.model.LinkedDevice
import com.vizio.redwolf.dms.model.LinkedDevicesList
import com.vizio.redwolf.dms.model.LinkedDevicesResult
import com.vizio.redwolf.dms.model.NonceRegisterResponse
import com.vizio.redwolf.dms.model.NonceRegisterResult
import com.vizio.redwolf.dms.model.PayNonceRequestResponse
import com.vizio.redwolf.dms.model.PayNonceRequestResult
import com.vizio.redwolf.dms.model.PayNonceStatusFieldedResponse
import com.vizio.redwolf.dms.model.PayNonceStatusFieldedResult
import com.vizio.redwolf.dms.model.PayNonceStatusResponse
import com.vizio.redwolf.dms.model.PayNonceStatusResult
import com.vizio.redwolf.dms.model.RegistrationStatusRequestInfo
import com.vizio.redwolf.dms.model.RegistrationStatusResponse
import com.vizio.redwolf.dms.model.RegistrationStatusResult
import com.vizio.redwolf.dms.model.RequestNonceRequestInfo
import com.vizio.redwolf.dms.model.UnregisterAllResponse
import com.vizio.redwolf.dms.model.UnregisterAllResult
import com.vizio.redwolf.dms.model.VoiceLinkingConfig
import com.vizio.redwolf.dms.model.VoiceLinkingResponse
import com.vizio.redwolf.dms.model.VoiceLinkingResult
import com.vizio.redwolf.utils.AppConfig
import com.vizio.redwolf.utils.ClientConfig
import com.vizio.redwolf.utils.ECommerceEnvironmentConfig
import com.vizio.redwolf.utils.HomeSetupEnvironmentConfig
import com.vizio.redwolf.utils.HttpResult
import com.vizio.redwolf.utils.PayEnvironment
import com.vizio.redwolf.utils.ResponseHeaders
import com.vizio.redwolf.utils.TimeoutException
import com.vizio.redwolf.utils.choosePayEnvironment
import com.vizio.redwolf.utils.clientprovider.ClientConfigFactory
import com.vizio.redwolf.utils.clientprovider.WebApiUseCase
import com.vizio.redwolf.utils.generateRandomString
import com.vizio.redwolf.utils.retryRequest
import io.ktor.client.call.body
import io.ktor.client.plugins.HttpRequestTimeoutException
import io.ktor.http.HttpStatusCode
import io.ktor.http.isSuccess
import kotlinx.serialization.PolymorphicSerializer
import kotlinx.serialization.SerializationException
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.encodeToJsonElement
import kotlin.js.JsExport

class HomeSetupApi(
    private val homeSetupEnvironment: HomeSetupEnvironment,
    private val payEnvironment: PayEnvironment,
    private val appConfig: AppConfig,
    private val clientConfig: ClientConfig = ClientConfigFactory.getClientConfig(WebApiUseCase.SDK_HEADERS)
) {

    private val homeSetupClient = HomeSetupHttpClient(env = homeSetupEnvironment, appConfig = appConfig, clientConfig = clientConfig)
    private val accountApi = AccountApi(payEnvironment, appConfig)

    constructor(homeSetupEnvConfig: HomeSetupEnvironmentConfig, appConfig: AppConfig) : this(
        chooseHomeSetupEnvironment(homeSetupEnvConfig),
        choosePayEnvironment(ECommerceEnvironmentConfig(environmentGroup = homeSetupEnvConfig.environmentGroup, apiKey = homeSetupEnvConfig.eCommApiKey, messageTokenAESKeyMasked = null)),
        appConfig = appConfig
    )

    constructor(homeSetupEnvConfig: HomeSetupEnvironmentConfig, appConfig: AppConfig, clientConfig: ClientConfig) : this(
        chooseHomeSetupEnvironment(homeSetupEnvConfig),
        choosePayEnvironment(
            ECommerceEnvironmentConfig(
                environmentGroup = homeSetupEnvConfig.environmentGroup,
                apiKey = homeSetupEnvConfig.eCommApiKey,
                messageTokenAESKeyMasked = null
            )
        ),
        appConfig,
        clientConfig
    )

    constructor(homeSetupEnvConfig: HomeSetupEnvironmentConfig, appConfig: AppConfig, timeout: Long) : this(
        chooseHomeSetupEnvironment(homeSetupEnvConfig),
        choosePayEnvironment(
            ECommerceEnvironmentConfig(
                environmentGroup = homeSetupEnvConfig.environmentGroup,
                apiKey = homeSetupEnvConfig.eCommApiKey,
                messageTokenAESKeyMasked = null
            )
        ),
        appConfig,
        ClientConfigFactory.getClientConfig(WebApiUseCase.SDK_HEADERS, timeout)
    )

    /**
     * Request a Home linking pin for given device information
     * @param requestInfo RequestNonceRequestInfo
     * @param retryOnRegistered if set to true, if DIID is already registered, it unlinked and requests nonce again
     *
     * @return [PayNonceRequestResponse] with pin and registration url on success.
     */
    suspend fun requestNonce(
        requestInfo: RequestNonceRequestInfo,
        retryOnRegistered: Boolean = false,
        sentryHeader: String? = null,
        tvAppId: String? = null,
        traceId: String? = null
    ): PayNonceRequestResult {
        return retryHomeSetupRequest {
            when (val result = homeSetupClient.requestNonce(requestInfo, sentryHeader, tvAppId, traceId)) {
                is HttpResult.Success -> {
                    val httpResponse = result.data
                    val statusCode = result.data.status.value
                    val headers = ResponseHeaders.Valid(statusCode)
                    if (httpResponse.status.isSuccess() || httpResponse.status.value == HttpStatusCode.BadRequest.value) {
                        val payNonceRequestResponse = try {
                            val response = httpResponse.body<JsonElement>()
                            NonceJsonConfig.config.decodeFromJsonElement(
                                PolymorphicSerializer(PayNonceRequestResponse::class),
                                response
                            )
                        } catch (ex: SerializationException) {
                            PayNonceRequestResponse.Error(ex)
                        } catch (ex: Exception) {
                            // To avoid TV to not crash, but instead get full error trace
                            PayNonceRequestResponse.Error(ex)
                        }
                        if (retryOnRegistered && payNonceRequestResponse is PayNonceRequestResponse.AlreadyRegistered) {
                            retryRequestNonceWithUnlink(requestInfo)
                        } else {
                            PayNonceRequestResult(headers, payNonceRequestResponse)
                        }
                    } else {
                        val payNonceRequestResponse = PayNonceRequestResponse.Failed(httpResponse.status.description)
                        PayNonceRequestResult(headers, payNonceRequestResponse)
                    }
                }

                is HttpResult.Error -> {
                    val headers = ResponseHeaders.Invalid
                    val exception = result.exception
                    if (exception is TimeoutException) {
                        PayNonceRequestResult(headers, PayNonceRequestResponse.Timeout(exception))
                    } else {
                        PayNonceRequestResult(ResponseHeaders.Invalid, PayNonceRequestResponse.Error(exception))
                    }
                }
            }
        }
    }

    suspend fun updateVoiceLinking(
        voiceLinkingConfig: VoiceLinkingConfig,
        deviceInstanceId: String,
        sentryHeader: String? = null,
        tvAppId: String? = null,
        traceId: String? = null
    ): VoiceLinkingResult {
        val voiceConfigJson = Json.encodeToJsonElement(voiceLinkingConfig)
        return updateVoiceLinking(voiceConfigJson, deviceInstanceId, sentryHeader, tvAppId, traceId)
    }

    suspend fun updateVoiceLinking(voiceRequestBody: JsonElement, deviceInstanceId: String, sentryHeader: String? = null, tvAppId: String? = null, traceId: String? = null): VoiceLinkingResult {
        return retryHomeSetupRequest {
            when (val result = homeSetupClient.updateVoiceLinking(voiceRequestBody, deviceInstanceId, sentryHeader, tvAppId, traceId)) {
                is HttpResult.Success -> {
                    val httpResponse = result.data
                    val headers = ResponseHeaders.Valid(httpResponse.status.value)
                    val voiceLinkingResponse = if (httpResponse.status.isSuccess()) {
                        try {
                            val successResponse = httpResponse.body<VoiceLinkingResponse.Success>()
                            successResponse
                        } catch (ex: SerializationException) {
                            VoiceLinkingResponse.Error(ex)
                        } catch (ex: Exception) {
                            VoiceLinkingResponse.Error(ex)
                        }
                    } else {
                        try {
                            val response = result.data.body<JsonElement>()
                            NonceJsonConfig.config.decodeFromJsonElement(PolymorphicSerializer(VoiceLinkingResponse::class), response)
                        } catch (ex: SerializationException) {
                            VoiceLinkingResponse.Error(ex)
                        } catch (ex: Exception) {
                            // To avoid TV to not crash, but instead get full error trace
                            VoiceLinkingResponse.Error(ex)
                        }
                    }
                    VoiceLinkingResult(headers, voiceLinkingResponse)
                }

                is HttpResult.Error -> {
                    val headers = ResponseHeaders.Invalid
                    val exception = result.exception
                    if (exception is TimeoutException) {
                        VoiceLinkingResult(headers, VoiceLinkingResponse.Timeout(exception))
                    } else {
                        VoiceLinkingResult(ResponseHeaders.Invalid, VoiceLinkingResponse.Error(exception))
                    }
                }
            }
        }
    }

    suspend fun tryRequestNonce(
        requestInfo: RequestNonceRequestInfo,
        retryOnRegistered: Boolean = false,
        sentryHeader: String? = null,
        tvAppId: String? = null,
        traceId: String? = null
    ): PayNonceRequestResult =
        try {
            requestNonce(requestInfo, retryOnRegistered, sentryHeader, tvAppId, traceId)
        } catch (exception: HttpRequestTimeoutException) {
            val headers = ResponseHeaders.Invalid
            val payNonceRequestResponse = PayNonceRequestResponse.Failed(exception.message.toString())
            PayNonceRequestResult(headers, payNonceRequestResponse)

        }

    /**
     * Request the status of a given Home linking pin for fielded TVs
     * @param requestInfo FieldedStatusRequestInfo
     * @return [PayNonceStatusFieldedResponse] with registration status and device information on success.
     */
    suspend fun requestNonceStatusFielded(requestInfo: FieldedStatusRequestInfo, sentryHeader: String? = null, tvAppId: String? = null): PayNonceStatusFieldedResult {
        return retryHomeSetupRequest {
            when (val result = homeSetupClient.requestNonceStatusFielded(requestInfo, sentryHeader, tvAppId)) {
                is HttpResult.Success -> {
                    val statusCode = result.data.status.value
                    val headers = ResponseHeaders.Valid(statusCode)
                    val payNonceStatusResponse = try {
                        val response = result.data.body<JsonElement>()
                        NonceJsonConfig.config.decodeFromJsonElement(PolymorphicSerializer(PayNonceStatusFieldedResponse::class), response)
                    } catch (ex: SerializationException) {
                        PayNonceStatusFieldedResponse.Error(ex)
                    } catch (ex: Exception) {
                        PayNonceStatusFieldedResponse.Error(ex)
                    }
                    PayNonceStatusFieldedResult(headers, payNonceStatusResponse)
                }

                is HttpResult.Error -> {
                    val headers = ResponseHeaders.Invalid
                    val exception = result.exception
                    if (exception is TimeoutException) {
                        PayNonceStatusFieldedResult(headers, PayNonceStatusFieldedResponse.Timeout(exception))
                    } else {
                        PayNonceStatusFieldedResult(ResponseHeaders.Invalid, PayNonceStatusFieldedResponse.Error(exception))
                    }
                }
            }
        }
    }

    /**
     * Link a Home linking pin to an authenticated Vizio user
     * @param myVizioBearerToken String Authentication token
     * @param pin String Home linking pin
     *
     * @return [NonceRegisterResponse] with status and device info on success.
     */
    suspend fun linkNonce(myVizioBearerToken: String, pin: String, sentryHeader: String? = null, tvAppId: String? = null, traceId: String? = null): NonceRegisterResult {
        val state = generateRandomString(LENGTH_STATE)
        val redirectUri = homeSetupEnvironment.redirectUri
        val accountAuthCodeRequestInfo = AccountAuthCodeRequestInfo(
            accessToken = myVizioBearerToken,
            clientId = CLIENT_ID_AUTH_CODE,
            redirectUri = redirectUri,
            state = state
        )
        return retryHomeSetupRequest {
            when (val authCodeResult = accountApi.requestAuthCode(accountAuthCodeRequestInfo)) {
                is AccountResult.Success -> {
                    val authCode = authCodeResult.data.authCode
                    val linkNonceRequestInfo = LinkNonceRequestInfo(
                        authorizationCode = authCode,
                        pendingDevicePin = pin,
                        state = state
                    )
                    when (val result = homeSetupClient.linkNonce(linkNonceRequestInfo, sentryHeader, tvAppId, traceId)) {
                        is HttpResult.Success -> {
                            val statusCode = result.data.status.value
                            val headers = ResponseHeaders.Valid(statusCode)

                            val nonceRegisterResponse = try {
                                val response = result.data.body<JsonElement>()
                                NonceJsonConfig.config.decodeFromJsonElement(PolymorphicSerializer(NonceRegisterResponse::class), response)
                            } catch (ex: SerializationException) {
                                NonceRegisterResponse.Error(ex)
                            } catch (ex: Exception) {
                                NonceRegisterResponse.Error(ex)
                            }
                            NonceRegisterResult(headers, nonceRegisterResponse)
                        }

                        is HttpResult.Error -> {
                            val headers = ResponseHeaders.Invalid
                            val exception = result.exception
                            if (exception is TimeoutException) {
                                NonceRegisterResult(headers, NonceRegisterResponse.Timeout(exception))
                            } else {
                                NonceRegisterResult(ResponseHeaders.Invalid, NonceRegisterResponse.Error(exception))
                            }
                        }
                    }
                }

                is AccountResult.Failed -> {
                    val headers = ResponseHeaders.Invalid
                    val failureMessage = "Failed: Unable to get auth code for token!"
                    val nonceRegisterResponse = NonceRegisterResponse.Failed(authCodeResult.response.detail ?: failureMessage)
                    NonceRegisterResult(ResponseHeaders.Invalid, nonceRegisterResponse)
                }

                is AccountResult.Error -> {
                    val headers = ResponseHeaders.Invalid
                    val exception = authCodeResult.exception
                    val nonceRegisterResponse = NonceRegisterResponse.Error(exception)
                    NonceRegisterResult(ResponseHeaders.Invalid, nonceRegisterResponse)
                }

                is AccountResult.Timeout -> {
                    val headers = ResponseHeaders.Invalid
                    val exception = authCodeResult.exception
                    val nonceRegisterResponse = NonceRegisterResponse.Timeout(exception)
                    NonceRegisterResult(ResponseHeaders.Invalid, nonceRegisterResponse)
                }
            }
        }
    }

    /**
     * Unlink the Home linking pin associated with a given device instance id
     * @param deviceInstanceId Device instance id on which to remove the link association.
     *
     * @return [UnregisterAllResponse] with status.
     */
    suspend fun unlink(deviceInstanceId: String, sentryHeader: String? = null, tvAppId: String? = null, traceId: String? = null): UnregisterAllResult {
        return retryHomeSetupRequest {
            when (val result = homeSetupClient.unlink(deviceInstanceId, sentryHeader, tvAppId, traceId)) {
                is HttpResult.Success -> {
                    val statusCode = result.data.status.value
                    val headers = ResponseHeaders.Valid(statusCode)
                    val unregisterAllResponse = try {
                        val response = result.data.body<JsonElement>()
                        NonceJsonConfig.config.decodeFromJsonElement(PolymorphicSerializer(UnregisterAllResponse::class), response)
                    } catch (ex: SerializationException) {
                        UnregisterAllResponse.Error(ex)
                    } catch (ex: Exception) {
                        UnregisterAllResponse.Error(ex)
                    }
                    UnregisterAllResult(headers, unregisterAllResponse)
                }

                is HttpResult.Error -> {
                    val headers = ResponseHeaders.Invalid
                    val exception = result.exception
                    if (exception is TimeoutException) {
                        UnregisterAllResult(headers, UnregisterAllResponse.Timeout(exception))
                    } else {
                        UnregisterAllResult(headers, UnregisterAllResponse.Error(exception))
                    }
                }
            }
        }
    }

    suspend fun getLinkedDevices(
        myVizioBearerToken: String,
        linkedDevicesFilter: LinkedDevicesFilter,
        sentryHeader: String? = null,
        tvAppId: String? = null,
        traceId: String? = null
    ): LinkedDevicesResult {
        return retryHomeSetupRequest {
            when (val result = homeSetupClient.getLinkedDevices(myVizioBearerToken, sentryHeader, tvAppId, traceId)) {
                is HttpResult.Success -> {
                    val httpResponse = result.data
                    val statusCode = result.data.status.value
                    val headers = ResponseHeaders.Valid(statusCode)
                    val linkedDevicesResponse = if (httpResponse.status.isSuccess()) {
                        try {
                            val devicesResponse = httpResponse.body<LinkedDevicesList>()
                            // Ensure devices are copied to a new list to guarantee immutability
                            val filteredDevices = filterLinkedDevices(devicesResponse.devices, linkedDevicesFilter)
                                .toList()
                            LinkedDevicesResponse.Success(filteredDevices)
                        } catch (ex: SerializationException) {
                            LinkedDevicesResponse.Error(ex)
                        } catch (ex: Exception) {
                            LinkedDevicesResponse.Error(ex)
                        }
                    } else {
                        try {
                            val failureMessage = httpResponse.body<LinkedDevicesFailureMessage>()
                            LinkedDevicesResponse.Failure(failureMessage)
                        } catch (ex: SerializationException) {
                            LinkedDevicesResponse.Error(ex)
                        } catch (ex: Exception) {
                            LinkedDevicesResponse.Error(ex)
                        }
                    }
                    LinkedDevicesResult(headers, linkedDevicesResponse)
                }

                is HttpResult.Error -> {
                    val headers = ResponseHeaders.Invalid
                    val exception = result.exception
                    if (exception is TimeoutException) {
                        LinkedDevicesResult(headers, LinkedDevicesResponse.Timeout(exception))
                    } else {
                        LinkedDevicesResult(headers, LinkedDevicesResponse.Error(exception))
                    }
                }
            }
        }
    }


    /**
     * Check current registration status for given device instance id
     * @param requestInfo Request Info to include device instance id for which to check registration status
     *
     * @return [RegistrationStatusResult] Representing the result of the registration status
     */
    suspend fun requestRegistrationStatus(requestInfo: RegistrationStatusRequestInfo, traceId: String? = null): RegistrationStatusResult {
        return retryHomeSetupRequest {
            when (val result = homeSetupClient.requestRegistrationStatus(requestInfo, traceId)) {
                is HttpResult.Success -> {
                    val httpResponse = result.data
                    val statusCode = httpResponse.status.value
                    val headers = ResponseHeaders.Valid(statusCode)
                    val registrationStatusResponse = if (httpResponse.status.isSuccess()) {
                        try {
                            val successResponse = httpResponse.body<RegistrationStatusResponse.Success>()
                            successResponse
                        } catch (ex: SerializationException) {
                            RegistrationStatusResponse.Error(ex)
                        } catch (ex: Exception) {
                            RegistrationStatusResponse.Error(ex)
                        }
                    } else {
                        try {
                            val failureMessage = httpResponse.body<RegistrationStatusResponse.Failure>()
                            failureMessage
                        } catch (ex: SerializationException) {
                            RegistrationStatusResponse.Error(ex)
                        } catch (ex: Exception) {
                            RegistrationStatusResponse.Error(ex)
                        }
                    }
                    RegistrationStatusResult(headers, registrationStatusResponse)
                }

                is HttpResult.Error -> {
                    val headers = ResponseHeaders.Invalid
                    val exception = result.exception
                    if (exception is TimeoutException) {
                        RegistrationStatusResult(headers, RegistrationStatusResponse.Timeout(exception))
                    } else {
                        RegistrationStatusResult(ResponseHeaders.Invalid, RegistrationStatusResponse.Error(exception))
                    }
                }
            }
        }
    }


    // TODO: This mitigation allows successive PIN requests regardless of device linked status.  To be phased out.
    private suspend fun retryRequestNonceWithUnlink(requestInfo: RequestNonceRequestInfo, sentryHeader: String? = null, traceId: String? = null): PayNonceRequestResult {
        return when (unlink(requestInfo.deviceInstanceId, sentryHeader, traceId = traceId).response) {
            is UnregisterAllResponse.UnregistrationSuccess -> requestNonce(requestInfo = requestInfo, retryOnRegistered = false)
            else -> PayNonceRequestResult(ResponseHeaders.Invalid, PayNonceRequestResponse.RetryFailed())

        }
    }

    private fun <T : Any> isHomeSetupSuccess(result: T): Boolean {
        return if (result is PayNonceRequestResult && result.response is PayNonceRequestResponse.Pending)
            true
        else if (result is PayNonceStatusResult && result.response is PayNonceStatusResponse.Registered)
            true
        else if (result is NonceRegisterResult && result.response is NonceRegisterResponse.RegistrationSuccess)
            true
        else if (result is UnregisterAllResult && result.response is UnregisterAllResponse.UnregistrationSuccess)
            true
        else result is LinkedDevicesResult && result.response is LinkedDevicesResponse.Success
    }

    private suspend inline fun <T : Any> retryHomeSetupRequest(block: () -> T): T {
        return retryRequest(clientConfig, block, isSuccess = ::isHomeSetupSuccess)
    }

    private fun filterLinkedDevices(devicesToFilter: List<LinkedDevice>, devicesFilter: LinkedDevicesFilter): List<LinkedDevice> {
        return when (devicesFilter) {
            LinkedDevicesFilter.All -> devicesToFilter
            LinkedDevicesFilter.Pay -> devicesToFilter.filter { it.vizioPayData?.linked ?: false }
            LinkedDevicesFilter.Legacy -> devicesToFilter.filter { it.vizioPayData?.linked == false }
            LinkedDevicesFilter.Alexa -> devicesToFilter.filter { it.alexaSynced }
            LinkedDevicesFilter.Google -> devicesToFilter.filter { it.googleAssistantSynced }
        }
    }

    companion object {
        private const val CLIENT_ID_AUTH_CODE = "VIZIO_TV"
        private const val LENGTH_STATE = 50
        private const val PATH_SETUP = "/setup"
    }
}

@JsExport
sealed class LinkedDevicesFilter {
    object All : LinkedDevicesFilter()
    object Pay : LinkedDevicesFilter()
    object Legacy : LinkedDevicesFilter()
    object Alexa : LinkedDevicesFilter()
    object Google : LinkedDevicesFilter()

    fun <R> fold(
        onAll: (All: All) -> R,
        onPay: (Pay: Pay) -> R,
        onLegacy: (Legacy: Legacy) -> R,
        onAlexa: (Alexa: Alexa) -> R,
        onGoogle: (Google: Google) -> R
    ): R {
        return when (this) {
            is All -> onAll(All)
            is Pay -> onPay(Pay)
            is Legacy -> onLegacy(Legacy)
            is Alexa -> onAlexa(Alexa)
            is Google -> onGoogle(Google)
        }
    }
}